import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";
import { capture, trackCta } from "@/lib/analytics";

const SESSION_KEY = "iag_exit_intent_shown_v1";
// Small minimum-engagement threshold so a visitor who just landed and
// accidentally flicks the mouse toward the tab strip doesn't get the
// prompt immediately — see PRD "Phase 3" for the reasoning.
const MIN_ENGAGEMENT_MS = 8000;

const isDesktopPointer = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

/**
 * Minimal, desktop-only exit-intent prompt. Mount this directly on the two
 * approved pages only (High-Stakes Presentation Coaching + Executive
 * Presence Development) — it renders nothing anywhere else, never
 * intercepts Back/beforeunload, and shows at most once per browser session
 * (sessionStorage-gated, shared across both pages).
 *
 * `variant` swaps which free tool gets promoted — "readiness_check"
 * (default, the Next Presentation Readiness Check, used on the
 * High-Stakes Presentation Coaching page) or "executive_presence" (the
 * Executive Presence Communication Diagnostic, used on the Executive
 * Presence Development page) — everything else (timing, modal shell,
 * dismiss behaviour) is identical between the two.
 */
const VARIANTS = {
  readiness_check: {
    titleKey: "exitIntent.title",
    bodyKey: "exitIntent.body",
    ctaKey: "exitIntent.cta",
    ctaHref: ROUTES.nextPresentationReadinessCheck,
    ctaTestId: "exit-intent-audit-cta",
    targetAssetId: "next_presentation_readiness",
  },
  executive_presence: {
    titleKey: "exitIntent.epTitle",
    bodyKey: "exitIntent.epBody",
    ctaKey: "exitIntent.epCta",
    ctaHref: ROUTES.executivePresenceCommunicationDiagnostic,
    ctaTestId: "exit-intent-ep-diagnostic-cta",
    targetAssetId: "executive_presence_communication",
  },
};

export const ExitIntentPrompt = ({ variant = "readiness_check" } = {}) => {
  const { t, l, lang } = useLang();
  const { titleKey, bodyKey, ctaKey, ctaHref, ctaTestId, targetAssetId } = VARIANTS[variant] || VARIANTS.readiness_check;
  const [open, setOpen] = useState(false);
  const eligibleRef = useRef(false);
  const shownRef = useRef(false);

  useEffect(() => {
    if (!isDesktopPointer()) return;
    let alreadyShown = false;
    try {
      alreadyShown = sessionStorage.getItem(SESSION_KEY) === "true";
    } catch {
      /* no-op */
    }
    if (alreadyShown) return;

    const engagementTimer = setTimeout(() => {
      eligibleRef.current = true;
    }, MIN_ENGAGEMENT_MS);

    const onMouseOut = (e) => {
      if (!eligibleRef.current || shownRef.current) return;
      // The reliable cross-browser exit-intent signal is `mouseout` on
      // `document` where the pointer has genuinely left the document
      // entirely (relatedTarget/toElement is null) — NOT `mouseleave` on
      // `document`, which does not fire for a real cursor reaching the top
      // edge of the viewport (confirmed by direct reproduction: genuine
      // pointer movement to y=0 never fired a document 'mouseleave' event;
      // only a synthetic dispatchEvent did). Requiring `clientY <= 20` too
      // keeps this specific to the top edge (not left/right/bottom exits).
      const leftDocument = !e.relatedTarget;
      const nearTopEdge = e.clientY <= 20;
      if (!leftDocument || !nearTopEdge) return;
      shownRef.current = true;
      try {
        sessionStorage.setItem(SESSION_KEY, "true");
      } catch {
        /* no-op */
      }
      setOpen(true);
      capture("exit_intent_shown", { page_path: window.location.pathname, language: lang, variant });
    };

    document.addEventListener("mouseout", onMouseOut);
    return () => {
      clearTimeout(engagementTimer);
      document.removeEventListener("mouseout", onMouseOut);
    };
  }, []);

  const dismiss = () => {
    setOpen(false);
    capture("exit_intent_dismissed", { page_path: window.location.pathname, language: lang, variant });
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-4 sm:p-6"
          style={{ background: "rgba(20,19,16,0.45)" }}
          onClick={dismiss}
          data-testid="exit-intent-overlay"
        >
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md bg-paper border border-ink/12 shadow-[0_28px_70px_rgba(0,0,0,0.35)] px-7 py-8"
            data-testid="exit-intent-modal"
          >
            <button
              type="button"
              onClick={dismiss}
              aria-label={t("exitIntent.dismiss")}
              data-testid="exit-intent-close"
              className="absolute top-4 right-4 text-ink-soft hover:text-terracotta transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h3 className="font-serif text-2xl leading-snug text-ink pr-6">
              {t(titleKey)}
            </h3>
            <p className="mt-3 font-body text-sm text-ink-soft leading-relaxed">
              {t(bodyKey)}
            </p>

            <div className="mt-6 flex flex-col gap-3">
              <Link
                to={l(ctaHref)}
                data-testid={ctaTestId}
                onClick={() =>
                  trackCta({
                    ctaId: ctaTestId,
                    location: "exit-intent",
                    href: ctaHref,
                    targetAssetId,
                  })
                }
                className="inline-flex items-center justify-center bg-ink text-paper px-6 py-3.5 font-body text-sm uppercase tracking-[0.16em] transition-colors duration-300 hover:bg-terracotta"
              >
                {t(ctaKey)}
              </Link>
              <button
                type="button"
                onClick={dismiss}
                data-testid="exit-intent-dismiss"
                className="font-body text-sm text-ink-soft underline decoration-terracotta/50 underline-offset-4 hover:text-terracotta transition-colors"
              >
                {t("exitIntent.dismiss")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
